import { useState } from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

// material
import { LoadingButton } from '@mui/lab';
import { Box, Stack, IconButton, InputAdornment, InputLabel, FormControl } from '@mui/material';

// graphql
import { RegisterUserGQLFunction } from '../../graphql/REGISTER_USER';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import { FormProvider, RHFTextField, RHFToggleButton, RHFSelect } from '../../components/hook-form';

// info
import { USER } from '../../info/USER';

export default function CreateRegisterUserForm() {
  const { t } = useTranslation();
  const [createuser] = useMutation(RegisterUserGQLFunction.CREATE_REGISTER_USER);
  const [isEmailRegistered] = useLazyQuery(RegisterUserGQLFunction.IS_EMAIL_REGISTERED);
  const [isUserIdRegistered] = useLazyQuery(RegisterUserGQLFunction.IS_USERID_REGISTERED);
  const navigate = useNavigate();

  const CreateUserSchema = Yup.object().shape({
    userId: Yup.string()
      .required(t('people.userid_req'))
      .test('isUserIdRegistered', t('people.userid_exists'), async (value) => {
        const result = await isUserIdRegistered({ variables: { userId: value } });
        return !result.data.isUserIdRegistered;
      }),
    authenticator: Yup.string()
      .email(t('login.email_valid'))
      .required(t('people.email_req'))
      .test('isEmailRegistered', t('people.email_exists'), async (value) => {
        if (!value) {
          return false;
        }
        const result = await isEmailRegistered({ variables: { authenticator: value } });
        return !result.data.isEmailRegistered;
      }),
    username: Yup.string().required(t('people.username_req')),
  });

  const defaultValues = {
    userId: '',
    authenticator: '',
    username: '',
    nickname: '',
    title: '',
    userType: '',
  };

  const methods = useForm({
    resolver: yupResolver(CreateUserSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setError,
  } = methods;

  const onSubmit = async (input) => {
    const { data } = await createuser({
      variables: {
        userId: input.userId,
        authenticator: input.authenticator,
        username: input.username,
        nickname: input.nickname,
        title: input.title,
        userType: +input.userType,
      },
    });

    const info = data.newRegisterUser.registerUser;
    if (info.err) {
      setError(info.err, { type: 'custom', message: info.msg });
    } else {
      navigate(`/register/info/${info.id}`);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3} sx={{ mb: 4 }}>
        <RHFTextField name="userId" label={t('common.user_id')} autoComplete="off" />
        <RHFTextField name="authenticator" label={t('common.email')} autoComplete="off" />
        <RHFTextField name="username" label={t('common.username')} autoComplete="off" />
        <RHFTextField name="nickname" label={t('common.nickname')} autoComplete="off" />
        <RHFTextField name="title" label={t('common.title')} autoComplete="off" />
        <FormControl sx={{ gridColumn: 'span 2' }}>
          <Box sx={{ mb: 2 }}>
            <InputLabel shrink>{t('common.user_type')}</InputLabel>
          </Box>
          <RHFToggleButton states={USER.USER_TYPE.map(type => t(`common.${type.toLowerCase()}`))} name="userType" />
        </FormControl>
      </Stack>

      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
        {t('people.create_btn', { identity: t('nav.users') })}
      </LoadingButton>
    </FormProvider>
  );
}
